import React from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight, BadgeCheck, CheckCircle2, MinusCircle, Sparkles } from 'lucide-react'
import MarketingSiteLayout from '../../marketing/MarketingSiteLayout'
import { LANDING_PATH, PUBLIC_INQUIRY_PATH } from '../../marketing/marketingNav'
import {
  EARLY_BIRD_LABEL,
  EARLY_BIRD_NOTE,
  formatKrw,
  listToSaleDiscountPercentRounded,
  marketingPlans as plans,
  planComparisonSections,
} from '../../content/publicPlans'

const planDetails = {
  basic: {
    title: '핵심 기능 시작',
    highlights: ['진료정보분석 월 5건', '숨은보험금찾기 월 5건', '통합리포트 월 5건', '보험금청구 제외'],
  },
  enterprise: {
    title: '전체 기능 무제한',
    highlights: ['보험금청구 무제한', '진료정보분석 무제한', '숨은보험금찾기 무제한', '통합리포트 무제한'],
  },
  'organization-contract': {
    title: '조직 단위 도입',
    highlights: ['조직배너·조직홈페이지 제공', '좌석·조직 구조 기준 견적', '계약 조건별 할인 적용', '조직 관리자 운영 지원'],
  },
}

const legend = '표시 금액은 1인 · 1개월 기준이며, 부가세와 프로모션 조건에 따라 달라질 수 있습니다.'

function PlanCard({ plan }) {
  const detail = planDetails[plan.id] || { title: plan.badge, highlights: [] }
  const hasListStrike =
    plan.listMonthlyKrw != null &&
    plan.saleMonthlyKrw != null &&
    Number(plan.saleMonthlyKrw) < Number(plan.listMonthlyKrw)
  const discountPct = hasListStrike ? listToSaleDiscountPercentRounded(plan.listMonthlyKrw, plan.saleMonthlyKrw) : 0
  const featured = Boolean(plan.featured)

  return (
    <article
      className={`relative flex min-h-[500px] flex-col rounded-lg border bg-white p-6 ${
        featured
          ? 'border-brand-navy shadow-[0_30px_90px_-68px_rgba(15,23,42,0.55)] ring-2 ring-brand-sky/20'
          : 'border-slate-200 shadow-[0_22px_70px_-62px_rgba(15,23,42,0.35)]'
      }`}
    >
      {featured ? (
        <span className="absolute -top-3 left-6 rounded-full bg-brand-navy px-4 py-1 text-xs font-bold text-white">
          추천
        </span>
      ) : null}
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs font-bold text-brand-sky">{plan.name}</p>
        {hasListStrike ? (
          <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-700">{EARLY_BIRD_LABEL}</span>
        ) : null}
      </div>
      <h2 className="mt-4 text-2xl font-bold text-slate-950">{detail.title}</h2>
      {plan.desc ? <p className="mt-3 min-h-[44px] text-sm font-medium leading-6 text-slate-600">{plan.desc}</p> : null}
      <div className="mt-6">
        {hasListStrike ? (
          <>
            <p className="text-sm font-semibold text-slate-400">
              정가 <span className="line-through">{formatKrw(plan.listMonthlyKrw)}</span>
            </p>
            <div className="mt-2 flex flex-wrap items-end gap-2">
              <span className="text-4xl font-bold tracking-normal text-slate-950">{formatKrw(plan.saleMonthlyKrw)}</span>
              {discountPct ? <span className="mb-1 rounded-full bg-emerald-50 px-2 py-1 text-xs font-bold text-emerald-700">{discountPct}% 할인</span> : null}
            </div>
          </>
        ) : (
          <span className="text-4xl font-bold tracking-normal text-slate-950">{plan.price}</span>
        )}
      </div>
      <p className="mt-2 text-sm font-medium text-slate-500">{plan.period}</p>
      {plan.annualHint ? <p className="mt-2 text-xs font-bold text-brand-navy">{plan.annualHint}</p> : null}

      <ul className="mt-8 grid gap-3 border-t border-slate-100 pt-6">
        {detail.highlights.map((item) => (
          <li key={item} className="flex items-center gap-2 text-sm font-medium text-slate-700">
            <CheckCircle2 className="h-4 w-4 shrink-0 text-brand-sky" aria-hidden />
            <span>{item}</span>
          </li>
        ))}
      </ul>

      <Link
        to={plan.cta.to}
        className={`mt-auto flex min-h-[48px] items-center justify-center rounded-md px-5 text-sm font-bold transition ${
          featured
            ? 'bg-brand-navy text-white hover:bg-sky-700'
            : 'border border-slate-300 bg-white text-slate-800 hover:border-sky-300 hover:bg-sky-50'
        }`}
      >
        <span>{plan.cta.label}</span>
        <ArrowRight className="ml-2 h-4 w-4" aria-hidden />
      </Link>
    </article>
  )
}

function ComparisonValue({ value, strong = false }) {
  if (value === '제공') {
    return (
      <span className={`inline-flex items-center justify-center gap-1 text-sm font-bold ${strong ? 'text-brand-navy' : 'text-slate-700'}`}>
        <CheckCircle2 className={`h-4 w-4 ${strong ? 'text-brand-navy' : 'text-brand-sky'}`} aria-hidden />
        제공
      </span>
    )
  }
  if (value === '미제공' || value === '제외' || value === '—') {
    return (
      <span className="inline-flex items-center justify-center gap-1 text-sm font-bold text-slate-400">
        <MinusCircle className="h-4 w-4" aria-hidden />
        {value === '제외' ? '제외' : '미제공'}
      </span>
    )
  }
  if (value === '일부 GA' || value === '조직계약 시' || value === '조직 계약 전용') {
    return <span className="whitespace-pre-line text-sm font-bold leading-6 text-slate-500">{value}</span>
  }
  return <span className={`whitespace-pre-line text-sm font-bold leading-6 ${strong ? 'text-brand-navy' : 'text-slate-700'}`}>{value}</span>
}

export default function PricingPage() {
  return (
    <MarketingSiteLayout>
      <section className="bg-white">
        <div className="mx-auto max-w-5xl px-4 py-16 text-center md:py-24">
          <p className="mx-auto inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-bold text-emerald-700">
            <Sparkles className="mr-2 h-4 w-4" aria-hidden />
            {EARLY_BIRD_LABEL}
          </p>
          <h1 className="mt-6 text-4xl font-bold leading-tight text-slate-950 sm:text-5xl">
            설계EZ 요금제
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-base font-medium leading-7 text-slate-600 sm:text-lg">
            BASIC은 청구를 제외한 핵심 분석 기능을 월 5건씩 제공하고,
            ENTERPRISE는 보험금청구와 주요 기능을 제한 없이 제공합니다. 조직 도입은 단체계약으로 별도 상담합니다.
          </p>
          <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
            <Link
              to={PUBLIC_INQUIRY_PATH}
              className="inline-flex min-h-[48px] items-center justify-center rounded-md bg-brand-navy px-6 text-sm font-bold text-white transition hover:bg-sky-700"
            >
              도입 상담하기
            </Link>
            <Link
              to={LANDING_PATH}
              className="inline-flex min-h-[48px] items-center justify-center rounded-md border border-slate-300 bg-white px-6 text-sm font-bold text-slate-800 transition hover:border-sky-300 hover:bg-sky-50"
            >
              설계EZ 소개
            </Link>
          </div>
        </div>
      </section>

      <section className="bg-[#f6f8fb] py-14 md:py-20">
        <div className="mx-auto max-w-7xl px-4">
          <div className="mb-8 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
            <div>
              <p className="text-sm font-bold text-brand-sky">플랜 선택</p>
              <h2 className="mt-2 text-3xl font-bold text-slate-950">월 구독과 단체계약을 함께 안내합니다.</h2>
            </div>
            <p className="inline-flex max-w-lg items-center gap-2 text-sm font-semibold leading-6 text-slate-600">
              <BadgeCheck className="h-5 w-5 shrink-0 text-emerald-600" aria-hidden />
              {EARLY_BIRD_NOTE}
            </p>
          </div>
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {plans.map((plan) => (
              <PlanCard key={plan.id} plan={plan} />
            ))}
          </div>
          <p className="mx-auto mt-8 max-w-3xl text-center text-xs font-medium leading-6 text-slate-500">{legend}</p>
        </div>
      </section>

      <section className="bg-white py-16 md:py-24">
        <div className="mx-auto max-w-6xl px-4">
          <div className="text-center">
            <p className="text-sm font-bold text-brand-sky">기능 비교</p>
            <h2 className="mt-3 text-3xl font-bold text-slate-950">메뉴 구조별 제공 범위</h2>
            <p className="mx-auto mt-4 max-w-2xl text-sm font-medium leading-6 text-slate-600">
              BASIC은 진료정보분석·숨은보험금찾기·통합리포트만 월 5건 제한을 두고, 그 외 기능은 제공합니다.
            </p>
          </div>

          <div className="mt-10 overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-[0_24px_80px_-70px_rgba(15,23,42,0.45)]">
            <table className="w-full min-w-[640px] table-fixed border-collapse text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="sticky left-0 z-10 w-[190px] bg-slate-50 px-4 py-4 text-left text-xs font-bold text-slate-500">기능</th>
                  <th className="w-[220px] px-4 py-4 text-center text-xs font-bold text-slate-700">BASIC</th>
                  <th className="w-[220px] bg-sky-50 px-4 py-4 text-center text-xs font-bold text-brand-navy">ENTERPRISE</th>
                </tr>
              </thead>
              <tbody>
                {planComparisonSections.map((section) => (
                  <React.Fragment key={section.title}>
                    <tr className="bg-slate-950">
                      <td colSpan={3} className="border-b border-slate-900 px-5 py-3 text-sm font-bold text-white">
                        {section.title}
                      </td>
                    </tr>
                    {section.rows.map((row) => (
                      <tr key={row.label} className="border-b border-slate-100">
                        <td className="sticky left-0 z-10 w-[190px] bg-white px-4 py-4 font-medium text-slate-800">{row.label}</td>
                        <td className="w-[220px] px-4 py-4 text-center"><ComparisonValue value={row.basic} /></td>
                        <td className="w-[220px] bg-sky-50/55 px-4 py-4 text-center"><ComparisonValue value={row.enterprise} strong /></td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-5 text-center text-xs font-medium leading-6 text-slate-500">
            ENTERPRISE의 무제한 제공은 정상적인 고객 상담 및 청구 업무 범위 내에서 적용됩니다. 조직배너·조직홈페이지는 별도 조직 계약 체결 시 제공됩니다.
          </p>
        </div>
      </section>

      <section className="bg-white pb-20 md:pb-24">
        <div className="mx-auto max-w-7xl px-4">
          <div className="grid gap-8 rounded-lg bg-gradient-to-r from-brand-navy to-brand-sky p-8 text-white md:grid-cols-[1fr_auto] md:items-center md:p-10">
            <div>
              <h2 className="text-3xl font-bold leading-tight">어떤 플랜이 맞을지 모르겠다면</h2>
              <p className="mt-4 max-w-xl text-sm font-medium leading-7 text-sky-50">
                사용하는 기능, 인원, 조직 운영 방식을 확인한 뒤 가장 적합한 도입 방식을 안내해드립니다.
              </p>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row">
              <Link
                to={PUBLIC_INQUIRY_PATH}
                className="inline-flex min-h-[48px] items-center justify-center rounded-md bg-white px-7 text-sm font-bold text-brand-navy transition hover:bg-sky-50"
              >
                도입 상담하기
              </Link>
              <Link
                to="/signup"
                className="inline-flex min-h-[48px] items-center justify-center rounded-md border border-white/40 bg-white/10 px-7 text-sm font-bold text-white transition hover:bg-white/20"
              >
                시작하기
              </Link>
            </div>
          </div>
        </div>
      </section>
    </MarketingSiteLayout>
  )
}
